import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'
import {
  TableCell,
  TextField,
  Tooltip,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  MenuItem,
  Select,
} from '@mui/material'
import { AddOutlined as AddOutlinedIcon, Refresh } from '@mui/icons-material'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuthContext } from '../../hooks/useAuthContext'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {
  ACTIVITY_STATUSES_DASHBOARD,
  DYNAMIC_FIELD_TYPE,
  FORMATS,
  PROCESS_ACTIVITY_STATUSES,
  PROCESS_STATUSES,
} from '../../utility/constants'
import {
  ACTIVITY_TYPES,
  CURRENCY_COUNTRY_CODE,
  DROPDOWN_DEFAULT_VALUE,
  EXCEL_COLUMNS,
  FIELD_TYPES,
  REDIRECT_TIMER,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
} from '../../utility/constants'
import {
  getProcessDashboardReportData,
  getProcessStatusByReferenceNum,
} from '../../utility/dataEntry/formData'
import ProcessDashboardValues from '../../components/Dashboard/ProcessDashboardValues'
import ConfirmationBox from '../../components/Dashboard/ConfirmationBox'
import moment from 'moment'
import { containsKeys } from '../DataEntryPage/utils/filterUtils'
import { getFieldId } from '../DataEntryPage/utils/utils'
import { downloadExcel } from '../../utility/excelDownloadUtils'
import TableSetting from '../../components/Dashboard/PreviewComponent/TableSettings/TableSetting'
import { getColumnForSetting, getFieldForSetting } from '../DataEntryPage/utils/fieldUtil'
import DateRangePickerComponent from '../../components/Dashboard/DateRangePickerComponent'
import DateTimeRangePickerComponent from '../../components/Dashboard/DateTimeRangePickerComponent'
import NumberRangeComponent from '../../components/Dashboard/NumberRange'
import ListDropdown from '../../components/Dashboard/ListRange'
import CurrencyRangeComponent from '../../components/Dashboard/CurrencyRangeComponent'
import ClearableTextField from '../../components/Dashboard/ClearableTextField'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import { TimeCalculationCategories } from '../../utility/constants'
import { minutesToHours } from 'date-fns'
import { calculateEffectiveMinutes } from '../../utility/processDashboard/workHourCalculation'

const ProcessDashboard = () => {
  const searchTimer = useRef()
  const virtuosoRef = useRef(null)
  const lastScrollIndexRef = useRef(0)
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const ASSIGNEE_DROPDOWN_DEFAULT_TEXT = 'All Assignees'
  const confirmationText = 'Are you sure you want to create a new process workflow?'
  let axiosOptions = {}
  if (user) {
    axiosOptions = { headers: { Authorization: `Bearer ${user.token}` } }
  }

  const [processDashBoardData, setProcessDashBoardData] = useState({
    processTemplate: {},
    processData: [],
    fieldTemplatesAll: [],
    fieldTemplates: [],
    hiddenColumns: [],
    activityTemplatesAll: [],
    activityTemplates: [],
    hiddenActivityColumns: [],
  })
  const [filteredProcessData, setFilteredProcessData] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isCreateNewProcessButtonDisabled, setIsCreateNewProcessButtonDisabled] = useState(false)
  const [isCreateNewProcessButtonHidden, setIsCreateNewProcessButtonHidden] = useState(true)
  const [currencyForDashboardValues, setCurrencyForDashboard] = useState([])
  const [searchFields, setSearchFields] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [excelLoading, setExcelLoading] = useState(false)
  const [showFilterIcons, setShowFilterIcons] = useState(false)
  const [clearFilterInput, setClearFilterInput] = useState({})
  const [secondHeaderTop, setSecondHeaderTop] = useState(0)
  const [filterRowTop, setFilterRowTop] = useState(0)
  const [totalProcessCreated, setTotalProcessCreated] = useState(0)
  const tableHeadRef = useRef(null)
  const [processDataForProcessTemplate, setProcessDataForProcessTemplate] = useState([])
  const [loadAllData, setLoadAllData] = useState(false)
  const [loadAllDataWarningDialog, setLoadAllDataWarningDialog] = useState(false)
  const [companyDetails, setCompanyDetails] = useState({})
  const [usersList, setUsersList] = useState([])
  const [aggregatedProcessStatus, setAggregatedProcessStatus] = useState([])
  const [totalTimeTaken, setTotalTimeTaken] = useState([])
  const [timeStatType, setTimeStatType] = useState(TimeCalculationCategories.AVERAGE)
  const [activityStatType, setActivityStatType] = useState(TimeCalculationCategories.AVERAGE)
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [selectedUser, setSelectedUser] = useState(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
  const [modifiedByUsers, setModifiedByUsers] = useState([])
  const [tooltipOpen, setTooltipOpen] = useState(false)

  const getAlignmentForType = (type) => {
    const rightTypes = ['date', 'currency', 'number', 'datetime']
    return rightTypes.includes(type) ? 'right' : 'left'
  }

  const {
    processTemplate,
    processData,
    fieldTemplatesAll,
    fieldTemplates,
    hiddenColumns,
    activityTemplatesAll,
    activityTemplates,
    hiddenActivityColumns,
  } = processDashBoardData

  const processTemplateId = useParams().process_template_id
  const isFiltersOn = Object.keys(searchFields)?.length > 0
  const LOAD_ALL_DATA_LIMIT = 200

  const { dashboardTittle, cardIcons } = useResponsiveTypography()

  const defaultTableHeaders = useMemo(
    () => [
      { id: 1001, name: 'Reference Number', width: '7rem', data_structure: FIELD_TYPES.TEXT },
      { id: 1002, name: 'Status', width: '3rem', data_structure: FIELD_TYPES.LIST },
      { id: 1003, name: 'Process Initiated On', width: '6rem', data_structure: FIELD_TYPES.DATE },
      { id: 1004, name: 'Process Closed On', width: '6rem', data_structure: FIELD_TYPES.DATE },
      { id: 1005, name: 'Current Activity', width: '8rem', data_structure: FIELD_TYPES.LIST },
      { id: 1006, name: 'Current Assignee', width: '8rem', data_structure: FIELD_TYPES.LIST },
      { id: 1007, name: 'Activity Assigned On', width: '6rem', data_structure: FIELD_TYPES.DATE },
      {
        id: 1008,
        name: 'Time Taken (Work Days)',
        width: '7.5rem',
        data_structure: FIELD_TYPES.NUMBER,
      },
    ],
    [],
  )

  const defaultActivityTableHeaders = useMemo(
    () => [
      { id: 2001, name: 'Version', width: '7rem', data_structure: FIELD_TYPES.NUMBER },
      { id: 2002, name: 'Status', width: '3rem', data_structure: FIELD_TYPES.LIST },
      { id: 2006, name: 'Activity Assigned On', width: '6rem', data_structure: FIELD_TYPES.DATE },
      { id: 2003, name: 'Closed Date', width: '6rem', data_structure: FIELD_TYPES.DATE },
      { id: 2004, name: 'Assignee', width: '6rem', data_structure: FIELD_TYPES.LIST },
      { id: 2005, name: 'Work hours', width: '8rem', data_structure: FIELD_TYPES.NUMBER },
    ],
    [],
  )

  const grayHeaderColor = '#f7f9ff'
  const INFO_COLUMN_WIDTH = 50
  const SECOND_COLUMN_LEFT = INFO_COLUMN_WIDTH

  // UPDATE STICKY HEADER POSITIONS
  useEffect(() => {
    const updateStickyPositions = () => {
      if (tableHeadRef.current) {
        const rows = tableHeadRef.current.querySelectorAll('tr')
        const firstRowHeight = rows[0]?.offsetHeight || 0
        const secondRowHeight = rows[1]?.offsetHeight || 0
        setSecondHeaderTop(firstRowHeight)
        setFilterRowTop(firstRowHeight + secondRowHeight)
      }
    }
    updateStickyPositions()
    const observer = new ResizeObserver(updateStickyPositions)
    if (tableHeadRef.current) observer.observe(tableHeadRef.current)
    return () => observer.disconnect()
  }, [showFilterIcons, fieldTemplatesAll, activityTemplatesAll])

  // SEARCH & FILTER LOGIC
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      const filters = Object.entries(searchFields)

      if (filters.length === 0) {
        setFilteredProcessData([])
        return
      }

      const currencyMap = new Map(
        currencyForDashboardValues.map((c) => [c.process_reference_num, c]),
      )

      const result = []

      for (let i = 0; i < processData.length; i++) {
        const row = processData[i]
        let isMatch = true

        for (let f = 0; f < filters.length; f++) {
          const [sfKey, sfValue] = filters[f]
          const colIndex = Number(sfKey.split('_')[1])
          const cell = row[colIndex + 1]

          if (!cell) {
            isMatch = false
            break
          }

          if (cell.fieldType === FIELD_TYPES.CURRENCY) {
            const currencyObj = currencyMap.get(cell.process_reference_num)

            if (!containsKeys(cell.value, sfValue, cell.fieldType, currencyObj)) {
              isMatch = false
              break
            }
          } else if (Array.isArray(cell.value)) {
            let match = false
            for (let k = 0; k < sfValue.length; k++) {
              const sv = String(sfValue[k]).trim()
              if (cell.value.some((v) => String(v).trim() === sv)) {
                match = true
                break
              }
            }
            if (!match) {
              isMatch = false
              break
            }
          } else {
            if (!containsKeys(cell.value, sfValue, cell.fieldType)) {
              isMatch = false
              break
            }
          }
        }
        if (isMatch) result.push(row)
      }
      setFilteredProcessData(result)
    }, 100)
    return () => clearTimeout(searchTimer.current)
  }, [searchFields, processData, currencyForDashboardValues])

  useEffect(() => {
    if (processTemplateId != null && user) {
      ;(async () => await getDataFromServer(LOAD_ALL_DATA_LIMIT))()
    }
  }, [processTemplateId, user])

  useEffect(() => {
    const originalOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
      document.documentElement.style.overflow = originalHtmlOverflow
    }
  }, [])

  const defaultActivity = activityTemplatesAll[0]?.activity_template_name ?? null
  const isAnalyticsActive =
    selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT ||
    selectedActivity !== defaultActivity ||
    activityStatType !== TimeCalculationCategories.AVERAGE

  useEffect(() => {
    setTooltipOpen(isAnalyticsActive)
  }, [isAnalyticsActive])

  useEffect(() => {
    if (Object.keys(searchFields).length === 0) return
    setSelectedActivity(activityTemplatesAll[0]?.activity_template_name ?? null)
    setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
    setActivityStatType(TimeCalculationCategories.AVERAGE)
    setTimeStatType(TimeCalculationCategories.AVERAGE)
  }, [searchFields, activityTemplatesAll])

  useEffect(() => {
    if (activityTemplatesAll.length > 0 && selectedActivity === null) {
      setSelectedActivity(activityTemplatesAll[0].activity_template_name)
    }
  }, [activityTemplatesAll, selectedActivity])

  const getUserProcessDashboardConfig = (ptId) => {
    try {
      const configs = JSON.parse(localStorage.getItem('processDashboardConfigs') || '[]')
      return configs.find((item) => item.process_template_id === ptId)
    } catch {
      return null
    }
  }

  const getActivityData = (processReferenceNum, allProcessData, activityId, holidayList) => {
    const filteredActivityData = allProcessData
      .filter(
        (pd) =>
          pd.process_reference_num == processReferenceNum && pd.activity_template_id == activityId,
      )
      .sort((a, b) => a.id - b.id)

    if (filteredActivityData.length > 0) {
      const latestData = filteredActivityData[filteredActivityData.length - 1]
      const totalWorkMinutes = filteredActivityData.reduce((total, activity) => {
        const startTime = new Date(activity.created_at)
        const endTime =
          activity.status === PROCESS_ACTIVITY_STATUSES.ACTIVE || !activity.modified_at
            ? new Date()
            : new Date(activity.modified_at)
        return total + calculateEffectiveMinutes(startTime, endTime, holidayList)
      }, 0)
      return {
        version: latestData.version,
        status:
          latestData.status?.toLowerCase() === PROCESS_ACTIVITY_STATUSES.ACTIVE
            ? ACTIVITY_STATUSES_DASHBOARD.ACTIVE
            : latestData.status
              ? ACTIVITY_STATUSES_DASHBOARD.CLOSED
              : '',
        assignedAt: latestData?.created_at ?? '',
        closedDate:
          latestData.status === PROCESS_ACTIVITY_STATUSES.ACTIVE
            ? ''
            : latestData.modified_at || '',
        assignee: latestData.modified_by_user || '',
        workHours: Number(minutesToHours(totalWorkMinutes).toFixed(2)),
      }
    }
    return {
      version: '',
      status: '',
      assignedAt: '',
      closedDate: '',
      assignee: '',
      workHours: null,
    }
  }

  const getDataFromServer = async (limit) => {
    setIsLoading(true)
    if (limit === LOAD_ALL_DATA_LIMIT) {
      toast.warning('Limited to 200 most recent records on initial page load.', {
        ...TOAST_SETTINGS,
        toastId: 'load-limit-warning',
      })
    }
    try {
      const processRelatedData = (
        await axios.get(
          `${NODE_BASE_URL}/process-dashboard/${processTemplateId}?limit=${limit}`,
          axiosOptions,
        )
      ).data

      const processTemplateResult = processRelatedData.processTemplate
      setModifiedByUsers([
        ...new Set(processRelatedData.processData.map((mb) => mb.modified_by_user).filter(Boolean)),
      ])
      const listDataForCompanyResult = processRelatedData.listData
      const usersAndTeamsDynamicList = processRelatedData.dynamicListData
      setUsersList(usersAndTeamsDynamicList?.usersList || [])
      let fieldTemplatesResult = processRelatedData.processDashboardFields
      const processDataForProcessTemplate = processRelatedData.processData
      const processInternalVisibility = processRelatedData.processInternalVisibility
      const isViewAllowed = processRelatedData.isViewAllowed
      const isInitiateAllowed = processRelatedData.isInitiationAllowed
      const currencyForDashboard = processRelatedData.nonDefaultCurrencies
      const activityTemplates = processRelatedData.activityTemplates
        .filter(
          (activity) =>
            [
              ACTIVITY_TYPES.DATA_ENTRY,
              ACTIVITY_TYPES.REVIEW,
              ACTIVITY_TYPES.LONG_RUNNING,
            ].includes(activity.activity_type) &&
            activity.activity_type !== ACTIVITY_TYPES.PARALLEL_START &&
            activity.activity_type !== ACTIVITY_TYPES.PARALLEL_END,
        )
        .map((activity) => ({ ...activity, is_hidden: false }))

      const holidayList = processRelatedData.holidays
      const totalProcessCreated = processRelatedData.totalProcessCreated
      setCurrencyForDashboard(currencyForDashboard)
      setCompanyDetails(processRelatedData.companyDetails)
      setIsCreateNewProcessButtonHidden(!isInitiateAllowed)
      setTotalProcessCreated(totalProcessCreated)

      if (!isViewAllowed) {
        navigate('/process-dashboard/')
        return
      }

      let processDataObj = {}
      processDataForProcessTemplate.forEach((pdRow) => {
        for (const [key, value] of Object.entries(pdRow.data)) {
          if (!key.includes('__')) processDataObj[key + '~' + pdRow.process_reference_num] = value
        }
      })

      let processDataArr = []
      for (const [key, value] of Object.entries(processDataObj)) {
        const [keyWithoutRefNum, processReferenceNum] = key.split('~')
        const index = parseInt(processReferenceNum)
        if (!processDataArr[index]) processDataArr[index] = {}
        processDataArr[index][keyWithoutRefNum] = value
      }

      const checkVisibility = (pd) => {
        let returnValue = true
        if (processInternalVisibility?.length > 0) {
          const currentUserTeams = user?.teams
          Object.keys(pd).forEach((key) => {
            const fieldId = key.split('__')[0].split('_')[1]
            processInternalVisibility?.forEach((piv) => {
              if (
                fieldId == piv?.is_private_field_template_id &&
                pd[key] == piv?.is_private_list_data_id
              ) {
                const allowedTeams = pd[getFieldId(piv?.private_teams_list_field_template_id)]
                if (Array.isArray(allowedTeams)) {
                  returnValue = !allowedTeams.some((val) => currentUserTeams?.includes(val))
                } else {
                  returnValue = !currentUserTeams.includes(allowedTeams)
                }
              }
            })
          })
        }
        return returnValue
      }

      let processDataArr2 = []
      let aggregatedProcessStatusLocal = []
      let totalTimeTakenLocal = []

      for (let i = 0; i < processDataArr.length; i++) {
        if (processDataArr[i] == null || !checkVisibility(processDataArr[i])) continue

        const processStatus = getProcessStatusByReferenceNum(i, processDataForProcessTemplate)
        aggregatedProcessStatusLocal.push(processStatus)

        const processInitiatedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'initiatedAt',
        )
        const processClosedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'closedAt',
        )
        const currentTask = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'currentTask',
        )
        const currentAssignee = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'currentAssignee',
        )
        const TaskAssignedAt = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'taskAssignedAt',
        )
        const calenderTime = getProcessDashboardReportData(
          i,
          processDataForProcessTemplate,
          'workingTime',
          holidayList,
        )
        totalTimeTakenLocal.push(calenderTime)

        let processDataRow = [
          {
            value: processTemplateResult[0].prefix + '-' + i,
            fieldType: null,
            is_hidden: false,
            process_reference_num: i,
          },
          { value: processStatus, fieldType: null, is_hidden: false, process_reference_num: i },
          {
            value: processInitiatedAt,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
          },
          { value: processClosedAt, fieldType: 'date', is_hidden: false, process_reference_num: i },
          {
            value: Array.isArray(currentTask) ? currentTask : [],
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
          },
          {
            value: Array.isArray(currentAssignee) ? currentAssignee : [],
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
          },
          { value: TaskAssignedAt, fieldType: 'date', is_hidden: false, process_reference_num: i },
          { value: calenderTime, fieldType: 'number', is_hidden: false, process_reference_num: i },
        ]

        const getListValue = (list, currentListId, is_hidden, id) => {
          const listData = list.find((it) => it.id == currentListId)
          processDataRow.push({
            value: listData?.value ?? '',
            fieldType: FIELD_TYPES.LIST,
            is_hidden,
            id,
            process_reference_num: i,
          })
        }

        for (let j = 0; j < fieldTemplatesResult.length; j++) {
          const ft = fieldTemplatesResult[j]
          const rawVal = processDataArr[i]['f_' + ft.id]

          if (ft.data_structure === FIELD_TYPES.LIST) {
            if (rawVal == DROPDOWN_DEFAULT_VALUE || rawVal == undefined) {
              processDataRow.push({
                value: '',
                fieldType: FIELD_TYPES.LIST,
                is_hidden: ft.is_hidden,
                id: ft.id,
                process_reference_num: i,
              })
            } else if (ft.dynamic_field_type != null) {
              const meta = ft.dynamic_field_type
              if (meta?.type == DYNAMIC_FIELD_TYPE.TEAMS)
                getListValue(usersAndTeamsDynamicList.teamsList, rawVal, ft.is_hidden, ft.id)
              else if (meta?.type == DYNAMIC_FIELD_TYPE.USERS)
                getListValue(usersAndTeamsDynamicList.usersList, rawVal, ft.is_hidden, ft.id)
            } else {
              getListValue(listDataForCompanyResult, rawVal, ft.is_hidden, ft.id)
            }
          } else if (ft.data_structure === FIELD_TYPES.LIST_MULTI) {
            if (rawVal == DROPDOWN_DEFAULT_VALUE || rawVal == undefined) {
              processDataRow.push({
                value: '',
                fieldType: FIELD_TYPES.LIST_MULTI,
                is_hidden: ft.is_hidden,
                id: ft.id,
                process_reference_num: i,
              })
            } else if (ft.dynamic_field_type != null) {
              const meta = ft.dynamic_field_type
              const srcList =
                meta?.type == DYNAMIC_FIELD_TYPE.TEAMS
                  ? usersAndTeamsDynamicList.teamsList
                  : usersAndTeamsDynamicList.usersList
              if (Array.isArray(rawVal)) {
                const val = rawVal
                  .map((ele) => {
                    const found = srcList.find((it) => it.id == ele)
                    if (!found) return null
                    return meta?.type == DYNAMIC_FIELD_TYPE.USERS
                      ? found.value.split('(')[0].trim()
                      : found.value
                  })
                  .filter(Boolean)
                processDataRow.push({
                  value: val,
                  fieldType: FIELD_TYPES.LIST_MULTI,
                  is_hidden: ft.is_hidden,
                  id: ft.id,
                  process_reference_num: i,
                })
              }
            } else if (Array.isArray(rawVal)) {
              const val = rawVal
                .map((ele) => listDataForCompanyResult.find((lst) => lst.id == ele)?.value)
                .filter(Boolean)
              processDataRow.push({
                value: val,
                fieldType: FIELD_TYPES.LIST_MULTI,
                is_hidden: ft.is_hidden,
                id: ft.id,
                process_reference_num: i,
              })
            } else {
              processDataRow.push({
                value: '',
                fieldType: FIELD_TYPES.LIST_MULTI,
                is_hidden: ft.is_hidden,
                id: ft.id,
                process_reference_num: i,
              })
            }
          } else if (ft.data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
            processDataRow.push({
              value: Array.isArray(rawVal) ? rawVal : [],
              fieldType: ft.data_structure,
              is_hidden: ft.is_hidden,
              id: ft.id,
              process_reference_num: i,
            })
          } else {
            processDataRow.push({
              value: rawVal ? rawVal.toString() : '',
              fieldType: ft.data_structure,
              is_hidden: ft.is_hidden,
              id: ft.id,
              process_reference_num: i,
            })
          }
        }

        for (let activity of activityTemplates) {
          const ad = getActivityData(i, processDataForProcessTemplate, activity.id, holidayList)
          processDataRow.push({
            value: ad.version,
            id: activity.id,
            fieldType: 'number',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          processDataRow.push({
            value: ad.status,
            id: activity.id,
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          processDataRow.push({
            value: ad.assignedAt,
            id: activity.id,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          processDataRow.push({
            value: ad.closedDate,
            id: activity.id,
            fieldType: 'date',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          processDataRow.push({
            value: ad.assignee,
            id: activity.id,
            fieldType: 'list',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
          processDataRow.push({
            value: ad.workHours,
            id: activity.id,
            fieldType: 'number',
            is_hidden: false,
            process_reference_num: i,
            is_Activity: true,
          })
        }
        processDataArr2.push(processDataRow)
      }
      setProcessDataForProcessTemplate(processDataForProcessTemplate)
      setAggregatedProcessStatus(aggregatedProcessStatusLocal)
      setTotalTimeTaken(totalTimeTakenLocal)

      processDataArr2 = processDataArr2.reverse()
      for (let pdRow of processDataArr2) {
        pdRow.unshift({
          id: pdRow[0].id,
          value: pdRow[0].value.toString().split('-')[1],
          fieldType: null,
        })
      }

      const localConfig = getUserProcessDashboardConfig(processTemplateId)
      const hiddenFieldIds = localConfig?.hidden_field_ids || []
      const visibleActivityIds = localConfig?.visible_activity_template_ids || []
      const hiddenActivityIds = activityTemplates
        .map((it) => it.id)
        .filter((it) => !visibleActivityIds.includes(it))

      setProcessDashBoardData({
        processTemplate: processTemplateResult[0],
        processData: processDataArr2,
        fieldTemplatesAll: fieldTemplatesResult,
        fieldTemplates: fieldTemplatesResult.filter(
          (it) => !hiddenFieldIds.includes(it.id) && !it.is_hidden,
        ),
        hiddenColumns: hiddenFieldIds,
        activityTemplatesAll: activityTemplates,
        activityTemplates: activityTemplates.filter((it) => visibleActivityIds.includes(it.id)),
        hiddenActivityColumns: hiddenActivityIds,
      })

      setSelectedActivity((prev) =>
        prev === null && activityTemplates.length > 0
          ? activityTemplates[0].activity_template_name
          : prev,
      )
    } catch (error) {
      console.error('getDataFromServer Error', error)
    }
    setIsLoading(false)
  }

  const handleCreateNewProcess = async () => {
    setIsDialogOpen(false)
    setIsCreateNewProcessButtonDisabled(true)
    try {
      const { process_id, activity_template_id } = (
        await axios.post(
          `${NODE_BASE_URL}/data-entry/process-data`,
          { process_template_id: processTemplateId },
          axiosOptions,
        )
      ).data
      toast.success('New process initiated successfully.', TOAST_SETTINGS)
      setTimeout(
        () => navigate(`/process-data/${process_id}/${activity_template_id}`),
        REDIRECT_TIMER,
      )
    } catch (err) {
      toast.error('An error occurred while initiating the new process.', TOAST_SETTINGS_ERROR)
      setIsCreateNewProcessButtonDisabled(false)
    }
  }

  const handleTableEndReached = useCallback(() => {
    if (
      !loadAllData &&
      totalProcessCreated > processData.length &&
      totalProcessCreated > LOAD_ALL_DATA_LIMIT
    ) {
      lastScrollIndexRef.current = processData.length - 1 // save last index
      setLoadAllDataWarningDialog(true)
    }
  }, [loadAllData, totalProcessCreated, processData.length, LOAD_ALL_DATA_LIMIT])

  const handleSearchFieldsChange = useCallback((e) => {
    const { name, value } = e.target
    setSearchFields((prev) => ({
      ...prev,
      [name]: value,
    }))
  }, [])

  const handleShowFilters = () => {
    setShowFilterIcons((prev) => !prev)
    if (showFilterIcons) {
      setSearchFields({})
      setFilteredProcessData(processData)
    } else if (
      !loadAllData &&
      totalProcessCreated >= processData.length &&
      totalProcessCreated > LOAD_ALL_DATA_LIMIT
    ) {
      setLoadAllDataWarningDialog(true)
    }
  }

  const handleLoadAllData = async () => {
    setLoadAllDataWarningDialog(false)
    await getDataFromServer(0)
    setLoadAllData(true)

    requestAnimationFrame(() => {
      virtuosoRef.current?.scrollToIndex({
        index: lastScrollIndexRef.current,
        behavior: 'auto',
      })
    })
  }

  const isFilterApplied = useCallback(
    (index) => Boolean(searchFields[`sf_${index}`]),
    [searchFields],
  )

  const onClearFilter = useCallback(
    (index) => {
      setClearFilterInput((prev) => ({
        ...prev,
        [index]: true,
      }))
      setSearchFields((prev) => {
        const next = { ...prev }
        delete next['sf_' + index]
        return next
      })
      setFilteredProcessData(processData)
      requestAnimationFrame(() => {
        setClearFilterInput((prev) => ({
          ...prev,
          [index]: false,
        }))
      })
    },
    [processData],
  )

  const renderFilterComponent = useCallback(
    (index, dataStructure) => {
      if (!showFilterIcons) return null

      switch (dataStructure) {
        case FIELD_TYPES.BIGTEXT:
        case FIELD_TYPES.TEXT:
          return (
            <ClearableTextField
              id={'sf_' + index}
              name={'sf_' + index}
              variant="outlined"
              color="primary"
              size="small"
              value={searchFields['sf_' + index] || ''}
              onChange={handleSearchFieldsChange}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 25 } }}
              clear={clearFilterInput[index] || false}
            />
          )

        case FIELD_TYPES.TIMESTAMPED_BIGTEXT:
          return (
            <TextField
              id={'sf_' + index}
              name={'sf_' + index}
              variant="outlined"
              color="primary"
              size="small"
              value={searchFields['sf_' + index] || ''}
              onChange={handleSearchFieldsChange}
              fullWidth
              sx={{ '& .MuiInputBase-root': { height: 25 } }}
            />
          )

        case FIELD_TYPES.DATE:
          return (
            <DateRangePickerComponent
              id={'sf_' + index}
              onChange={(range) =>
                setSearchFields((prev) => ({
                  ...prev,
                  ['sf_' + index]: range,
                }))
              }
              clear={clearFilterInput[index] || false}
              value={searchFields['sf_' + index]}
            />
          )

        case FIELD_TYPES.DATETIME:
          return (
            <DateTimeRangePickerComponent
              id={'sf_' + index}
              onChange={(range) =>
                setSearchFields((prev) => ({
                  ...prev,
                  ['sf_' + index]: range,
                }))
              }
              clear={clearFilterInput[index] || false}
            />
          )

        case FIELD_TYPES.LIST_MULTI:
        case FIELD_TYPES.LIST: {
          const searchFieldsWithoutSelf = Object.fromEntries(
            Object.entries(searchFields).filter(([k]) => k !== 'sf_' + index),
          )

          const hasOtherFilters = Object.keys(searchFieldsWithoutSelf).length > 0

          const dataForCounts = hasOtherFilters
            ? processData.filter((pd) =>
                Object.entries(searchFieldsWithoutSelf).every(([sfKey, sfValue]) => {
                  const colIndex = Number(sfKey.split('_')[1])
                  const cell = pd[colIndex + 1]

                  if (!cell) return false

                  if (cell.fieldType === FIELD_TYPES.CURRENCY) {
                    const cco = currencyForDashboardValues.find(
                      (c) => c.process_reference_num == cell.process_reference_num,
                    )

                    return containsKeys(cell.value, sfValue, cell.fieldType, cco)
                  } else if (Array.isArray(cell.value)) {
                    return sfValue.some((sv) =>
                      cell.value.some((cv) => String(cv).trim() === String(sv).trim()),
                    )
                  } else {
                    return containsKeys(cell.value, sfValue, cell.fieldType)
                  }
                }),
              )
            : processData

          const options = processData
            .reduce((acc, pd) => {
              const cellValue = pd[index + 1]?.value
              if (!cellValue) return acc
              const values = Array.isArray(cellValue) ? cellValue : [cellValue]
              values.forEach((val) => {
                if (!val || typeof val === 'object') return
                const strVal = String(val).trim()
                if (!strVal || acc.find((o) => o.value === strVal)) return
                const activeCount = dataForCounts.filter((ad) => {
                  const v = ad[index + 1]?.value
                  if (!v) return false
                  return Array.isArray(v)
                    ? v.some((x) => String(x).trim() === strVal)
                    : String(v).trim() === strVal
                }).length
                acc.push({
                  value: strVal,
                  count: activeCount,
                })
              })
              return acc
            }, [])
            .sort((a, b) => a.value.localeCompare(b.value))

          return (
            <ListDropdown
              options={options}
              onApply={(value) => {
                const selectedValues = value.map((item) => item.value.trim())
                setSearchFields((prev) => {
                  const n = { ...prev }
                  if (selectedValues.length > 0) n['sf_' + index] = selectedValues
                  else delete n['sf_' + index]
                  return n
                })
                setClearFilterInput((prev) => ({
                  ...prev,
                  [index]: false,
                }))
              }}
              clear={clearFilterInput[index] || false}
              value={searchFields['sf_' + index]}
            />
          )
        }

        case FIELD_TYPES.NUMBER:
          return (
            <NumberRangeComponent
              id={'sf_' + index}
              onChange={(range) => {
                const [initial, last] = range
                setSearchFields((prev) => {
                  const n = { ...prev }
                  if (initial === null && last === null) delete n['sf_' + index]
                  else n['sf_' + index] = range
                  return n
                })
                setFilteredProcessData(
                  processData.filter((pd) => {
                    const v = pd[index + 1]?.value
                    return (initial === null || v >= initial) && (last === null || v <= last)
                  }),
                )
              }}
              clear={clearFilterInput[index] || false}
              value={searchFields['sf_' + index]}
            />
          )

        case FIELD_TYPES.CURRENCY:
          return (
            <CurrencyRangeComponent
              id={'sf_' + index}
              onChange={(filter) =>
                setSearchFields((prev) => ({
                  ...prev,
                  ['sf_' + index]: filter,
                }))
              }
              clear={clearFilterInput[index] || false}
              value={searchFields['sf_' + index]}
            />
          )

        default:
          return null
      }
    },
    [
      showFilterIcons,
      searchFields,
      clearFilterInput,
      processData,
      currencyForDashboardValues,
      handleSearchFieldsChange,
    ],
  )

  const handleSetHiddenColumns = async (hiddenFieldIds, hiddenActivityIds) => {
    if (user) await saveUserProcessDashBoardConfigs(user, hiddenFieldIds, hiddenActivityIds)
  }

  const saveUserProcessDashBoardConfigs = async (user, hiddenFieldIds, hiddenActivityIds) => {
    try {
      const visibleActivityIds = processDashBoardData.activityTemplatesAll
        ?.map((it) => it.id)
        ?.filter((it) => !hiddenActivityIds?.includes(it))
      updateFieldTemplate(hiddenFieldIds)
      updateActivityTemplate(hiddenActivityIds)
      const existingConfigs = JSON.parse(localStorage.getItem('processDashboardConfigs') || '[]')
      let updatedConfigs = existingConfigs.filter(
        (item) => item.process_template_id !== processTemplateId,
      )
      if (hiddenFieldIds?.length || visibleActivityIds?.length) {
        updatedConfigs.push({
          process_template_id: processTemplateId,
          hidden_field_ids: hiddenFieldIds,
          visible_activity_template_ids: visibleActivityIds,
        })
      }
      localStorage.setItem('processDashboardConfigs', JSON.stringify(updatedConfigs))
    } catch (error) {
      console.error(error)
    }
  }

  const updateFieldTemplate = (hiddenFieldIds = []) => {
    setProcessDashBoardData((state) => ({
      ...state,
      fieldTemplates: fieldTemplatesAll.filter(
        (it) => !hiddenFieldIds.includes(it.id) && !it.is_hidden,
      ),
      hiddenColumns: hiddenFieldIds,
    }))
  }

  const updateActivityTemplate = (hiddenActivityIds = []) => {
    setProcessDashBoardData((state) => ({
      ...state,
      activityTemplates: activityTemplatesAll.filter((it) => !hiddenActivityIds.includes(it.id)),
      hiddenActivityColumns: hiddenActivityIds,
    }))
  }

  const handleSetHiddenActivityColumns = async (hiddenActivityIds = []) => {
    if (user) await saveUserProcessDashBoardConfigs(user, hiddenColumns, hiddenActivityIds)
  }

  const resetAnalyticsFilters = () => {
    setSelectedActivity(activityTemplatesAll?.[0]?.activity_template_name || '')
    setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
    setActivityStatType(TimeCalculationCategories.AVERAGE)
    setTooltipOpen(false)
  }

  const exportToExcel = async () => {
    const dataToExport = isFiltersOn ? filteredProcessData : processData

    setExcelLoading(true)

    try {
      let columnIndex = 0

      const processMetaDataFrom = columnIndex
      const processMetaDataTo = processMetaDataFrom + defaultTableHeaders.length - 1

      columnIndex = processMetaDataTo

      const processDataFrom = columnIndex + 1
      const processDataTo = processDataFrom + fieldTemplates.length - 1

      columnIndex = processDataTo

      const primaryHeaders = [
        {
          name: 'Process Metadata',
          from: `${EXCEL_COLUMNS[processMetaDataFrom]}1`,
          to: `${EXCEL_COLUMNS[processMetaDataTo]}1`,
        },
        {
          name: 'Process Data',
          from: `${EXCEL_COLUMNS[processDataFrom]}1`,
          to: `${EXCEL_COLUMNS[processDataTo]}1`,
        },
      ]

      for (let i = 0; i < activityTemplates.length; i++) {
        const activity = activityTemplates[i]

        const activityFrom = columnIndex + 1
        const activityTo = activityFrom + defaultActivityTableHeaders.length - 1

        primaryHeaders.push({
          name: `Timeline : ${activity.activity_template_name}`,
          from: `${EXCEL_COLUMNS[activityFrom]}1`,
          to: `${EXCEL_COLUMNS[activityTo]}1`,
        })

        columnIndex = activityTo
      }

      const secondaryHeaders = [
        ...defaultTableHeaders.map((it) => it.name),
        ...(fieldTemplates?.map((ft) => ft.field_template_name) || []),
        ...(activityTemplates?.flatMap(() => defaultActivityTableHeaders.map((h) => h.name)) || []),
      ]

      const header = {
        [EXCEL_COLUMNS[0]]: { v: '' },
      }

      const secondaryHeader = {}

      for (let i = 0; i < secondaryHeaders.length; i++) {
        secondaryHeader[EXCEL_COLUMNS[i]] = {
          v: secondaryHeaders[i],
          s: { bold: true },
        }
      }

      const allRowsData = [header, secondaryHeader]

      // PRECOMPUTE USER MAP
      const userMap = new Map()

      for (let i = 0; i < (usersList?.length || 0); i++) {
        const user = usersList[i]

        userMap.set(user.id, user?.value?.replace(/\s*\([^)]*\)\s*/g, '').trim())
      }

      const getActualValue = (fieldType, value, currencyCode) => {
        switch (fieldType) {
          case FIELD_TYPES.DATE:
          case FIELD_TYPES.DATETIME: {
            const dv = Array.isArray(value) ? value[0] || '' : value

            if (!dv || !moment(dv).isValid()) {
              return { v: '' }
            }

            return {
              v: moment(dv).toDate(),
              t: 'd',
              numFmt: fieldType === FIELD_TYPES.DATE ? FORMATS.DATE : FORMATS.DATETIME,
            }
          }

          case FIELD_TYPES.CURRENCY: {
            const pv = parseFloat(value)

            if (isNaN(pv)) {
              return { v: null }
            }

            const sym = CURRENCY_COUNTRY_CODE[currencyCode] || ''

            return {
              v: pv,
              numFmt: sym ? `"${sym}" #,##0.00` : '"₹" #,##0.00',
            }
          }

          case FIELD_TYPES.NUMBER: {
            const pv = parseFloat(value)

            return {
              v: isNaN(pv) ? null : pv,
            }
          }

          case FIELD_TYPES.TIMESTAMPED_BIGTEXT: {
            if (!Array.isArray(value) || !value.length) {
              return { v: '' }
            }

            return {
              v: value
                .map((e) => {
                  const username = e.user_id ? userMap.get(e.user_id) : null

                  return `${e.timestamp}: ${e.text}${username ? ` - ${username}` : ''}`
                })
                .join('\n\n'),
            }
          }

          case FIELD_TYPES.LIST:
          case FIELD_TYPES.LIST_MULTI:
            return {
              v: Array.isArray(value) ? value.join(', ') : value,
            }

          default:
            return { v: value }
        }
      }

      for (let r = 0; r < dataToExport.length; r++) {
        const row = dataToExport[r]

        const data = {}

        let excelColIndex = 0

        for (let c = 1; c < row.length; c++) {
          const item = row[c]

          const isHidden = item.is_Activity
            ? hiddenActivityColumnsSet.has(item.id)
            : hiddenColumnsSet.has(item.id)

          if (isHidden) continue

          const currencyObj = currencyMap.get(item.process_reference_num)

          data[EXCEL_COLUMNS[excelColIndex]] = getActualValue(
            item.fieldType,
            item.value,
            currencyObj?.currency_code,
          )

          excelColIndex++
        }

        allRowsData.push(data)
      }

      downloadExcel(
        [{ name: 'Sheet1', data: allRowsData }],
        `${processTemplate?.process_template_name}-(${moment().format(FORMATS.DATE)})`,
        primaryHeaders,
      )
    } finally {
      setExcelLoading(false)
    }
  }

  const activityNamesArray = activityTemplates.map((t) => `TimeLine: ${t.activity_template_name}`)
  const activityBaseIndexMap = useMemo(() => {
    const map = {}

    activityTemplatesAll.forEach((a, idx) => {
      map[a.activity_template_name] =
        1 + defaultTableHeaders.length + fieldTemplatesAll.length + idx * 6
    })

    return map
  }, [activityTemplatesAll, fieldTemplatesAll.length, defaultTableHeaders.length])

  const currencyMap = useMemo(() => {
    return new Map(currencyForDashboardValues.map((c) => [c.process_reference_num, c]))
  }, [currencyForDashboardValues])

  const filteredUsersForSelectedActivity = useMemo(() => {
    const dataToUse = isFiltersOn ? filteredProcessData : processData

    if (!selectedActivity) return []

    const baseIndex = activityBaseIndexMap[selectedActivity]

    if (baseIndex === undefined) return []

    const users = new Set()

    for (let i = 0; i < dataToUse.length; i++) {
      const row = dataToUse[i]
      if (row[baseIndex + 5]?.value != null && row[baseIndex + 5]?.value !== '') {
        const assignee = row[baseIndex + 4]?.value
        if (assignee && assignee !== '') {
          users.add(assignee)
        }
      }
    }

    return Array.from(users)
  }, [isFiltersOn, filteredProcessData, processData, selectedActivity, activityBaseIndexMap])

  const hiddenColumnsSet = useMemo(() => {
    return new Set(hiddenColumns || [])
  }, [hiddenColumns])

  const hiddenActivityColumnsSet = useMemo(() => {
    return new Set(hiddenActivityColumns || [])
  }, [hiddenActivityColumns])

  const tableData = useMemo(() => {
    return isFiltersOn ? filteredProcessData : processData
  }, [isFiltersOn, filteredProcessData, processData])

  const processStats = useMemo(() => {
    const data = isFiltersOn ? filteredProcessData : processData

    let active = 0
    let closed = 0

    for (let i = 0; i < data.length; i++) {
      const status = data[i][2]?.value

      if (status === PROCESS_STATUSES.ACTIVE) {
        active++
      }

      if (
        status === PROCESS_STATUSES.CLOSED_ACCEPTED ||
        status === PROCESS_STATUSES.CLOSED_REJECTED
      ) {
        closed++
      }
    }

    return {
      active,
      closed,
    }
  }, [isFiltersOn, filteredProcessData, processData])

  const processTimeStats = useMemo(() => {
    const data = isFiltersOn ? filteredProcessData : processData
    const times = data.map((r) => parseFloat(r[8]?.value)).filter((v) => !isNaN(v) && v > 0)
    if (!times.length) {
      return '0 days'
    }
    let val
    if (timeStatType === TimeCalculationCategories.MINIMUM) {
      val = Math.min(...times)
    } else if (timeStatType === TimeCalculationCategories.MAXIMUM) {
      val = Math.max(...times)
    } else {
      val = times.reduce((a, b) => a + b, 0) / times.length
    }
    return `${val.toFixed(1)} wdays`
  }, [isFiltersOn, filteredProcessData, processData, timeStatType])

  const activityStats = useMemo(() => {
    const data = isFiltersOn ? filteredProcessData : processData
    const actIdx = activityTemplatesAll.findIndex(
      (a) => a.activity_template_name === selectedActivity,
    )
    if (actIdx === -1) {
      return {
        value: 'N/A',
        affectedRecords: 0,
      }
    }
    const base = 1 + defaultTableHeaders.length + fieldTemplatesAll.length + actIdx * 6
    let rows = data
    if (selectedUser !== ASSIGNEE_DROPDOWN_DEFAULT_TEXT) {
      rows = data.filter((r) => {
        const c = r[base + 4]

        if (!c) return false

        return Array.isArray(c.value) ? c.value.includes(selectedUser) : c.value === selectedUser
      })
    }

    const times = rows.map((r) => parseFloat(r[base + 5]?.value)).filter((v) => !isNaN(v) && v >= 0)

    if (!times.length) {
      return {
        value: 'N/A',
        affectedRecords: 0,
      }
    }

    let h

    if (activityStatType === TimeCalculationCategories.MINIMUM) {
      h = Math.min(...times)
    } else if (activityStatType === TimeCalculationCategories.MAXIMUM) {
      h = Math.max(...times)
    } else {
      h = times.reduce((a, b) => a + b, 0) / times.length
    }

    return {
      value: `${(h / 8.5).toFixed(1)} wdays`,
      affectedRecords: times.length,
    }
  }, [
    isFiltersOn,
    filteredProcessData,
    processData,
    selectedActivity,
    selectedUser,
    activityStatType,
    activityTemplatesAll,
    fieldTemplatesAll.length,
    defaultTableHeaders.length,
  ])

  const renderedActivityGroupHeaders = useMemo(() => {
    return activityNamesArray.map((name, index) => (
      <TableCell
        key={index}
        align="center"
        sx={{ backgroundColor: grayHeaderColor }}
        colSpan={defaultActivityTableHeaders.length}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bolder',
            letterSpacing: '4px',
            fontSize: '16px',
            color: '#6c47e4',
          }}
        >
          {name}
        </Typography>
      </TableCell>
    ))
  }, [activityNamesArray, grayHeaderColor, defaultActivityTableHeaders.length])

  const renderedDefaultHeaders = useMemo(() => {
    return defaultTableHeaders.map((header, i) => (
      <TableCell
        key={i}
        align="center"
        sx={{
          minWidth: header.width,
          backgroundColor: grayHeaderColor,
          ...(i === 0 && {
            position: 'sticky',
            left: SECOND_COLUMN_LEFT,
            zIndex: 5,
          }),
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            {header.name}
          </Typography>

          {showFilterIcons && (
            <Tooltip title={isFilterApplied(i) ? 'Clear Filters' : null}>
              <IconButton
                size="small"
                onClick={() => {
                  if (isFilterApplied(i)) onClearFilter(i)
                }}
              >
                {isFilterApplied(i) ? (
                  <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                ) : (
                  <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    ))
  }, [
    defaultTableHeaders,
    grayHeaderColor,
    showFilterIcons,
    isFilterApplied,
    onClearFilter,
    SECOND_COLUMN_LEFT,
  ])

  const renderedFieldHeaders = useMemo(() => {
    return (
      fieldTemplatesAll?.length > 0 &&
      fieldTemplatesAll.map(
        (f, i) =>
          !f?.is_hidden &&
          !hiddenColumnsSet.has(f.id) && (
            <TableCell
              key={i + defaultTableHeaders.length}
              id={i + defaultTableHeaders.length}
              align="center"
              sx={{
                minWidth: `${f.field_width_in_rem_for_process_dashboard}rem`,
                backgroundColor: grayHeaderColor,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {f?.field_template_name}
                </Typography>

                {showFilterIcons && (
                  <Tooltip
                    title={
                      isFilterApplied(i + defaultTableHeaders.length)
                        ? 'Filter Applied'
                        : 'Enable Filter'
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isFilterApplied(i + defaultTableHeaders.length)) {
                          onClearFilter(i + defaultTableHeaders.length)
                        }
                      }}
                    >
                      {isFilterApplied(i + defaultTableHeaders.length) ? (
                        <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                      ) : (
                        <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          ),
      )
    )
  }, [
    fieldTemplatesAll,
    hiddenColumnsSet,
    defaultTableHeaders.length,
    grayHeaderColor,
    showFilterIcons,
    isFilterApplied,
    onClearFilter,
  ])

  const renderedActivityHeaders = useMemo(() => {
    let visibleActivityIndex = 0

    return activityTemplatesAll.map((data) => {
      if (hiddenActivityColumnsSet.has(data.id)) return null

      const currentActivityIndex = visibleActivityIndex++

      return defaultActivityTableHeaders.map((header, i) => {
        const filterIndex =
          i +
          defaultTableHeaders.length +
          fieldTemplatesAll.length +
          currentActivityIndex * defaultActivityTableHeaders.length

        return (
          <TableCell
            key={`${data.id}-${i}`}
            align={getAlignmentForType(header.data_structure)}
            sx={{
              minWidth: header.width,
              backgroundColor: grayHeaderColor,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {header.name}
              </Typography>

              {showFilterIcons && (
                <Tooltip title={isFilterApplied(filterIndex) ? 'Filter Applied' : 'Enable Filter'}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (isFilterApplied(filterIndex)) {
                        onClearFilter(filterIndex)
                      }
                    }}
                  >
                    {isFilterApplied(filterIndex) ? (
                      <FilterAltOffOutlinedIcon sx={{ fontSize: '18px' }} />
                    ) : (
                      <FilterAltOutlinedIcon sx={{ fontSize: '18px' }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </TableCell>
        )
      })
    })
  }, [
    activityTemplatesAll,
    hiddenActivityColumnsSet,
    defaultActivityTableHeaders,
    defaultTableHeaders.length,
    fieldTemplatesAll.length,
    grayHeaderColor,
    showFilterIcons,
    isFilterApplied,
    onClearFilter,
  ])

  return (
    <>
      <div className="process-dashbaord" style={{ marginTop: '-10px' }}>
        <div style={{ padding: '0px 5px 10px 0px', width: '100%' }}>
          {isLoading ? null : (
            <div
              className="process-group-icon"
              style={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography
                    className="textPrimary fontWeight700"
                    style={{ fontSize: dashboardTittle, fontWeight: 'bold' }}
                  >
                    {processTemplate.process_template_name}
                  </Typography>
                </Box>
                <Box className="processdata-group-icon" style={{ fontSize: '14px' }}>
                  <div style={cardIcons}>
                    <div hidden={isCreateNewProcessButtonHidden}>
                      <Tooltip title="Initiate new process workflow">
                        <IconButton
                          disabled={isCreateNewProcessButtonDisabled}
                          onClick={() => setIsDialogOpen(true)}
                          size="large"
                          style={{ marginLeft: '5px', padding: '2px' }}
                        >
                          <AddOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <Tooltip
                      title={
                        isFiltersOn
                          ? `Download (${filteredProcessData.length} rows filtered)`
                          : totalProcessCreated > processData.length
                            ? `Download (showing ${processData.length} of ${totalProcessCreated})`
                            : 'Download all data'
                      }
                    >
                      <IconButton
                        onClick={exportToExcel}
                        size="large"
                        style={{ margin: '0px 5px 0px 0px', padding: '2px' }}
                      >
                        {excelLoading ? (
                          <CircularProgress sx={{ fontSize: '20px', color: '#41464a' }} />
                        ) : (
                          <FileDownloadOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                    <TableSetting
                      columns={getFieldForSetting(fieldTemplatesAll)}
                      hiddenColumns={processDashBoardData.hiddenColumns}
                      setHiddenColumns={(it) =>
                        handleSetHiddenColumns(it, processDashBoardData.hiddenActivityColumns)
                      }
                      activityColumns={getColumnForSetting(activityTemplatesAll)}
                      hiddenActivityColumns={processDashBoardData.hiddenActivityColumns}
                      setHiddenActivityColumns={handleSetHiddenActivityColumns}
                      color="primary"
                    />
                    {!loadAllData ? (
                      <Tooltip title="Load all data to enable filters">
                        <IconButton
                          size="large"
                          onClick={() => setLoadAllDataWarningDialog(true)}
                          style={{ marginLeft: '5px', padding: '2px' }}
                        >
                          <Refresh sx={{ fontSize: '20px', color: '#41464a' }} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={showFilterIcons ? 'Clear All Filters' : 'Enable Filter'}>
                        <IconButton
                          size="large"
                          onClick={handleShowFilters}
                          style={{ marginLeft: '5px', padding: '2px' }}
                        >
                          {showFilterIcons ? (
                            <FilterAltOffOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                          ) : (
                            <FilterAltOutlinedIcon sx={{ fontSize: '20px', color: '#41464a' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </Box>
              </Box>

              {/* METRIC BOXES */}
              <Box
                className="process-dashboard-metrices"
                sx={{ marginTop: '2px', display: 'flex', gap: '8px', alignItems: 'stretch' }}
              >
                <Paper
                  elevation={2}
                  sx={{ p: '10px 12px', borderRadius: '12px', backgroundColor: '#f7f9ff', flex: 1 }}
                >
                  <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                    Processes
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '2px', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box sx={{ display: 'flex', gap: '4px' }}>
                        <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>Rows:</Typography>
                        <Typography sx={{ fontSize: '11px' }}>
                          <span style={{ fontWeight: 700 }}>
                            {isFiltersOn ? filteredProcessData.length : processData.length}
                          </span>
                          <span style={{ fontWeight: 400 }}> of {totalProcessCreated}</span>
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                          <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                            Active:
                          </Typography>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                            {processStats.active}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: 'flex', gap: '4px', position: 'relative', left: '62px' }}
                        >
                          <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                            Closed:
                          </Typography>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                            {processStats.closed}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Select
                          value={timeStatType}
                          onChange={(e) => setTimeStatType(e.target.value)}
                          variant="standard"
                          disableUnderline
                          size="small"
                          sx={{
                            fontSize: '10px',
                            background: '#f3f4f6',
                            borderRadius: '6px',
                            height: '18px',
                            width: '50px',
                            minWidth: 'unset',
                            '& .MuiSelect-select': {
                              px: '6px',
                              py: '2px',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              paddingRight: '20px !important',
                            },
                            '& .MuiSelect-icon': { fontSize: '14px', right: '2px' },
                          }}
                        >
                          <MenuItem value={TimeCalculationCategories.AVERAGE}>Avg</MenuItem>
                          <MenuItem value={TimeCalculationCategories.MINIMUM}>Min</MenuItem>
                          <MenuItem value={TimeCalculationCategories.MAXIMUM}>Max</MenuItem>
                        </Select>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#6c47e4' }}>
                          {processTimeStats}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>

                <Tooltip
                  open={tooltipOpen}
                  placement="top"
                  title={
                    isAnalyticsActive ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <span>Activity filters applied</span>
                        <FilterAltOffOutlinedIcon
                          onClick={(e) => {
                            e.stopPropagation()
                            resetAnalyticsFilters()
                          }}
                          sx={{ fontSize: '16px', cursor: 'pointer' }}
                        />
                      </Box>
                    ) : (
                      ''
                    )
                  }
                >
                  <div style={{ flex: 1, display: 'flex' }}>
                    <Paper
                      elevation={2}
                      sx={{
                        borderRadius: '10px',
                        padding: '10px 14px',
                        backgroundColor: isAnalyticsActive ? '#fff7ed' : '#f6f8fc',
                        border: isAnalyticsActive ? '1px solid #fb923c' : 'none',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>
                        Tasks
                      </Typography>
                      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <Select
                            value={selectedActivity ?? ''}
                            onChange={(e) => {
                              setSelectedActivity(e.target.value)
                              setSelectedUser(ASSIGNEE_DROPDOWN_DEFAULT_TEXT)
                            }}
                            variant="standard"
                            disableUnderline
                            size="small"
                            displayEmpty
                            renderValue={(s) => s || 'Activities'}
                            sx={{
                              fontSize: '10px',
                              background: '#f3f4f6',
                              borderRadius: '6px',
                              height: '18px',
                              width: '100px',
                              '& .MuiSelect-select': {
                                px: '6px',
                                py: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingRight: '20px !important',
                              },
                              '& .MuiSelect-icon': { fontSize: '14px', right: '2px' },
                            }}
                          >
                            {activityTemplatesAll.map((a) => (
                              <MenuItem key={a.id} value={a.activity_template_name}>
                                {a.activity_template_name}
                              </MenuItem>
                            ))}
                          </Select>
                          <Select
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                            variant="standard"
                            disableUnderline
                            size="small"
                            displayEmpty
                            renderValue={(s) => s || 'Assignees'}
                            sx={{
                              fontSize: '10px',
                              background: '#f3f4f6',
                              borderRadius: '6px',
                              height: '18px',
                              width: '100px',
                              '& .MuiSelect-select': {
                                px: '6px',
                                py: '2px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                paddingRight: '20px !important',
                              },
                              '& .MuiSelect-icon': { fontSize: '14px', right: '2px' },
                            }}
                          >
                            <MenuItem value={ASSIGNEE_DROPDOWN_DEFAULT_TEXT}>
                              All Assignees
                            </MenuItem>
                            {filteredUsersForSelectedActivity.map((u) => (
                              <MenuItem key={u} value={u}>
                                {u}
                              </MenuItem>
                            ))}
                          </Select>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: '4px',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Select
                              value={activityStatType}
                              onChange={(e) => setActivityStatType(e.target.value)}
                              variant="standard"
                              disableUnderline
                              size="small"
                              sx={{
                                fontSize: '10px',
                                background: '#f3f4f6',
                                borderRadius: '6px',
                                height: '18px',
                                width: '50px',
                                minWidth: 'unset',
                                '& .MuiSelect-select': {
                                  px: '6px',
                                  py: '2px',
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  paddingRight: '20px !important',
                                },
                                '& .MuiSelect-icon': { fontSize: '14px', right: '2px' },
                              }}
                            >
                              <MenuItem value={TimeCalculationCategories.AVERAGE}>Avg</MenuItem>
                              <MenuItem value={TimeCalculationCategories.MINIMUM}>Min</MenuItem>
                              <MenuItem value={TimeCalculationCategories.MAXIMUM}>Max</MenuItem>
                            </Select>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 700, color: '#6c47e4' }}
                            >
                              {activityStats.value}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }}>
                              Affected Records:
                            </Typography>
                            <Typography sx={{ fontSize: '11px', fontWeight: 700, lineHeight: 1.2 }}>
                              {activityStats.affectedRecords}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </div>
                </Tooltip>
              </Box>
            </div>
          )}
        </div>

        {isDialogOpen && (
          <ConfirmationBox
            title={confirmationText}
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            onYes={handleCreateNewProcess}
          />
        )}

        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh',
            }}
          >
            <CircularProgress color="primary" size={50} />
          </div>
        ) : (
          <ProcessDashboardValues
            processData={tableData}
            process_template_id={processTemplate.id}
            hiddenColumns={hiddenColumns}
            currencyForDashboardValues={currencyForDashboardValues}
            hiddenActivityColumns={hiddenActivityColumns}
            processTemplates={processTemplate}
            processDataForProcessTemplate={processDataForProcessTemplate}
            companyDetails={companyDetails}
            usersList={usersList}
            processDashBoardData={processDashBoardData}
            defaultTableHeaders={defaultTableHeaders}
            fieldTemplatesAll={fieldTemplatesAll}
            defaultActivityTableHeaders={defaultActivityTableHeaders}
            fieldTemplates={fieldTemplates}
            renderedActivityGroupHeaders={renderedActivityGroupHeaders}
            renderedDefaultHeaders={renderedDefaultHeaders}
            renderedFieldHeaders={renderedFieldHeaders}
            renderedActivityHeaders={renderedActivityHeaders}
            grayHeaderColor={grayHeaderColor}
            secondHeaderTop={secondHeaderTop}
            virtuosoRef={virtuosoRef}
            onEndReached={handleTableEndReached}
            filterRowTop={filterRowTop}
            showFilterIcons={showFilterIcons}
            renderFilterComponent={renderFilterComponent}
            hiddenColumnsSet={hiddenColumnsSet}
            hiddenActivityColumnsSet={hiddenActivityColumnsSet}
          />
        )}

        {loadAllDataWarningDialog && (
          <ConfirmationBox
            isDialogOpen={loadAllDataWarningDialog}
            setIsDialogOpen={setLoadAllDataWarningDialog}
            title="Load all data? This could take a while."
            onYes={handleLoadAllData}
          />
        )}
      </div>
    </>
  )
}

export default ProcessDashboard
